import { Typography } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatEUR } from 'utils/number';

interface PrimeRowProps {
  label: string;
  value: string | number | undefined;
  truncateValue?: boolean;
}

function PrimeRow({ label, value, truncateValue = false }: PrimeRowProps) {
  return (
    <div className="flex items-center justify-between border-b border-color-border px-3 py-2 text-sm leading-5 last:border-b-0">
      <span className="text-gris-hellio-6">{label}</span>
      <span
        className={`font-semibold text-gris-hellio-6 ${
          truncateValue
            ? 'w-[140px] min-w-0 truncate text-right md:w-[180px]'
            : ''
        }`}
        title={truncateValue ? String(value) : undefined}
      >
        {value}
      </span>
    </div>
  );
}

interface DotLegendProps {
  colorClassName: string;
  label: string;
  value: string;
}

function DotLegend({ colorClassName, label, value }: DotLegendProps) {
  return (
    <div className="flex items-center justify-between text-sm leading-5">
      <div className="flex items-center gap-2 text-gris-hellio-6">
        <span className={`h-1.5 w-1.5 rounded-full ${colorClassName}`} />
        <span>{label}</span>
      </div>
      <span className="font-semibold text-gris-hellio-6">{value}</span>
    </div>
  );
}

interface Step3MoraleCalculationSectionProps {
  showSlider: boolean;
  categorieVehicule: string | number | undefined;
  volumeMwhc: string;
  totalVolumeMwhc: string;
  nbVehicules: number;
  primeBeneficiaire: number;
  totalPrimeBeneficiaire: number;
  totalPrimeConcessionnaire: number;
  totalPrime: number;
  totalLabelKey: string;
}

export default function Step3MoraleCalculationSection({
  showSlider,
  categorieVehicule,
  volumeMwhc,
  totalVolumeMwhc,
  nbVehicules,
  primeBeneficiaire,
  totalPrimeBeneficiaire,
  totalPrimeConcessionnaire,
  totalPrime,
  totalLabelKey,
}: Step3MoraleCalculationSectionProps) {
  const { t } = useTranslation();

  return (
    <section className={!showSlider ? 'w-full' : ''}>
      <Typography.Title
        level={4}
        className="!mb-3 !text-[18px] !leading-7 !font-semibold !text-gris-hellio-6"
      >
        {t('nouveau_formulaire:resultat:calcul_de_la_prime')}
      </Typography.Title>

      <p className="mb-1.5 text-sm font-semibold leading-5 text-bleu-hellio-6">
        {t('nouveau_formulaire:resultat:par_vehicule')}
      </p>
      <div className="overflow-hidden rounded-md border border-color-border bg-white">
        <PrimeRow
          label={t('nouveau_formulaire:resultat:categorie')}
          value={categorieVehicule}
          truncateValue
        />
        <PrimeRow
          label={t('nouveau_formulaire:resultat:volume_de_CEE')}
          value={volumeMwhc}
        />
        <PrimeRow
          label={t('nouveau_formulaire:resultat:prime_cee')}
          value={primeBeneficiaire ? formatEUR(primeBeneficiaire) : '0'}
        />
      </div>

      <p className="mb-1.5 mt-5 text-sm font-semibold leading-5 text-bleu-hellio-6">
        {t('nouveau_formulaire:resultat:total')}
      </p>
      <div className="overflow-hidden rounded-md border border-color-border bg-white">
        <PrimeRow
          label={t('nouveau_formulaire:resultat:nombre_vehicules')}
          value={nbVehicules}
        />
        <PrimeRow
          label={t('nouveau_formulaire:resultat:volume_de_CEE')}
          value={totalVolumeMwhc}
        />
      </div>

      {showSlider && (
        <div className="mt-3 space-y-2">
          <DotLegend
            colorClassName="bg-grand-public-500"
            label={t('nouveau_formulaire:resultat:prime_concessionnaire')}
            value={formatEUR(totalPrimeConcessionnaire)}
          />
          <DotLegend
            colorClassName="bg-bleu-hellio-6"
            label={t('nouveau_formulaire:resultat:prime_beneficiaire')}
            value={formatEUR(totalPrimeBeneficiaire)}
          />
        </div>
      )}

      <div className="mt-3 flex items-center justify-between rounded-lg bg-bleu-hellio-1 px-3 py-2 text-base text-bleu-hellio-6">
        <span className="font-semibold">{t(totalLabelKey)}</span>
        <span className="font-semibold">{formatEUR(totalPrime)}</span>
      </div>
    </section>
  );
}
